/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { signUp_DLXAsync } from '../../actions/DataActionExtension.g';
import {
    determineSiteLanguageForAbandonedCart,
    getSiteLanguageConfigFromContext
} from '../../themes/venchi-v2-1/utils/determineSiteLanguageForAbandonedCart';
import { INewsletterBlockViewProps } from './newsletter-block';
import { customExternalAPI as customeExternalAPIType } from './newsletter-block.props.autogenerated';
import classnames from 'classnames';

export interface ExternalPayload {
    email: string;
    marketing: boolean;
    profiling: boolean;
    thirdparty: boolean;
    message?: string;
}

const NewsletterBlockView: React.FC<INewsletterBlockViewProps> = props => {
    const siteLanguageConfig = getSiteLanguageConfigFromContext(props.context);
    const localeString = siteLanguageConfig.currentLocale;
    const regionCRM = determineSiteLanguageForAbandonedCart(siteLanguageConfig);

    type Inputs = {
        email: string;
        marketing: boolean;
        profiling: boolean;
        thirdParts: boolean;
    };
    // const { register, handleSubmit, watch, errors } = useForm<Inputs>();
    const { register, handleSubmit, errors } = useForm<Inputs>();
    // const marketingChoice: boolean = watch('marketing');
    const [loading, setLoading] = React.useState(false);
    const [disabled_state, setDisabled] = React.useState(false);

    const customExternalAPI = props?.config?.customExternalAPI;
    const externalApiURL = props.config.externalApiURL ?? '';
    const externalThankYouMessage = props.config.externalThankYouMessage ?? '';
    const useExternalAPI = (externalApiURL && customExternalAPI === customeExternalAPIType.External) ?? false;

    let message = props.resources.verifyEmailText;
    // const channelId: number = props.context.request.apiSettings.channelId;
    const [subscribeMessage, setSubscribeMessage] = React.useState({
        state: false,
        message: '',
        class: ''
    });

    let source: string;
    switch (props.config.source?.toString()) {
        case 'CommerceSubscribersAU':
            source = 'Commerce Subscriber AU';
            break;
        case 'CommerceSubscriberEU':
            source = 'Commerce Subscriber EU';
            break;
        case 'CommerceSubscribersUS':
            source = 'Commerce Subscribers US';
            break;
        case 'CommerceSubscriberSG':
            source = 'Commerce Subscriber SG';
            break;
        case 'CommerceSubscriberHK':
            source = 'Commerce Subscriber HK';
            break;
        case 'RetailEU':
            source = 'Retail EU';
            break;
        case 'RetailUS':
            source = 'Retail US';
            break;
    }

    const _subscribeToNL = async (data: Inputs) => {
        setDisabled(true);
        setLoading(true);
        signUp_DLXAsync(
            { callerContext: props.context?.actionContext },
            '',
            '',
            data.email,
            '',
            false,
            data.marketing,
            localeString !== 'en-us' ? data.profiling : data.marketing,
            data.thirdParts,
            localeString,
            '',
            '',
            source,
            regionCRM
        )
            .then(result => {
                message = result.Error
                    ? result.Error === 'already_subscribed'
                        ? props.resources.alreadySubscribedText
                        : result.Error
                    : message;
                setLoading(false);
                setSubscribeMessage({
                    state: true,
                    message: message,
                    class: 'success'
                });
                // @ts-ignore
                window.dataLayer?.push?.({
                    event: 'gaEvent',
                    eventCategory: 'newsletter',
                    eventAction: 'subscription',
                    //@ts-ignore
                    eventLabel: props.config.position
                });
            })
            .catch(error => {
                message = error.message;
                setLoading(false);
                setSubscribeMessage({
                    state: true,
                    message: error.message,
                    class: 'error'
                });
            });
    };

    function postJson(url: string, data: any): Promise<number> {
        return fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.status;
            })
            .catch(error => {
                console.error('Error in newsletter-block.view.tsx - postJson:', error);
                throw error;
            });
    }

    const _externalSubscribeToNL = async (data: Inputs) => {
        setLoading(true);
        const payload: ExternalPayload = {
            email: data.email,
            marketing: data.marketing,
            profiling: data.profiling,
            thirdparty: data.thirdParts
        };
        postJson(externalApiURL, payload)
            .then(result => {
                setLoading(false);
                setSubscribeMessage({
                    state: true,
                    message: externalThankYouMessage,
                    class: 'success'
                });
            })
            .catch(error => {
                message = error.message;
                setLoading(false);
                setSubscribeMessage({
                    state: true,
                    message: error.message,
                    class: 'error'
                });
            });
    };

    const _validateEmail = (email: string): boolean | string => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email) || props.resources.invalidEmailAddressError;
    };

    return (
        <div
            className={classnames(props.className, 'container')}
            id={props.id}
            style={props.config.backgroundImage?.src ? { backgroundImage: `url(${props.config.backgroundImage.src})` } : undefined}
            {...props.renderModuleAttributes(props)}
        >
            {props.paragraph}
            {subscribeMessage.state === false ? (
                <form onSubmit={useExternalAPI ? handleSubmit(_externalSubscribeToNL) : handleSubmit(_subscribeToNL)}>
                    <div className={'email_row row'} aria-invalid={errors.email ? 'true' : 'false'}>
                        <input
                            name='email'
                            type='email'
                            ref={register({ required: true, validate: _validateEmail })}
                            placeholder={props.resources.yourEmailAddressText}
                            maxLength={320}
                        />
                        <button type='submit' disabled={disabled_state} className={`status__${subscribeMessage.class}`}>
                            {loading ? <div className={'loading'} /> : <span>{props.resources.subscribeBtnText}</span>}
                        </button>
                    </div>
                    {errors?.email?.message && (
                        <div className={'email-error_row row'}>
                            <p className='msc-alert msc-alert-danger' role='alert'>
                                {errors.email.message}
                            </p>
                        </div>
                    )}
                    <div className={'privacy_row row'}>
                        <div className={'inputBox privacy_field_ctn'} aria-invalid={errors.marketing ? 'true' : 'false'}>
                            <label>
                                <input
                                    ref={register({ required: true })}
                                    type={'checkbox'}
                                    name={'marketing'}
                                    defaultChecked={props.config.preflagMarketing ?? false}
                                    hidden={props.config.hideMarketing ?? false}
                                />
                                {props.privacyTextM}
                                {errors.marketing && (
                                    <p className='msc-alert msc-alert-danger' role='alert'>
                                        {props.config.requiredMarketingError ?? 'This checkbox is required'}
                                    </p>
                                )}
                            </label>
                        </div>
                    </div>
                    <div className={`privacy_row row row--PrivacyP ${props.config.hideProfiling && 'hidden'}`}>
                        <div className={'inputBox privacy_field_ctn'}>
                            <label>
                                <input
                                    ref={register()}
                                    type={'checkbox'}
                                    name={'profiling'}
                                    defaultChecked={props.config.preflagProfiling ?? false}
                                />
                                {props.privacyTextP}
                            </label>
                        </div>
                    </div>
                    <div className={`privacy_row row row--PrivacyT ${props.config.hideThirdParty && 'hidden'}`}>
                        <div className={'inputBox privacy_field_ctn'}>
                            <label>
                                <input
                                    ref={register()}
                                    type={'checkbox'}
                                    name={'thirdParts'}
                                    defaultChecked={props.config.preflagThirdParty ?? false}
                                />
                                {props.privacyTextT}
                            </label>
                        </div>
                    </div>
                </form>
            ) : (
                <div className={`msc-alert msc-alert-${subscribeMessage.class}`} role='alert'>
                    {subscribeMessage.message}
                </div>
            )}
        </div>
    );
};

export default NewsletterBlockView;
